import { useEstimatePaymentFee, useWithdrawalState, useWithdrawalActions, useWithdrawalValidation } from "src/hooks";
import { PaymentType, ToggleSwitch, VerificationType, PaymentsRoutesResponse } from "src/api";
import { useDepositWithdrawContext } from "src/contexts";
import { EWithdrawalStep } from "src/types";

export interface WithdrawalStep {
  currentStep: EWithdrawalStep;
  stepLoading: boolean;
  stepError: string | null;
}

export const useWithdrawal = ({ paymentsRoutes }: { paymentsRoutes: PaymentsRoutesResponse }) => {
  // Get context data
  const {
    balance,
    user: { mfa_status },
  } = useDepositWithdrawContext();
  const { currency } = balance;

  // State management
  const state = useWithdrawalState();
  const {
    amount,
    currentStep,
    isLoading,
    error,
    banksList,
    selectedBank,
    accountNumber,
    sessionData,
    mfaCode,
    setMfaCode,
    isRtcEnabled,
  } = state;

  // Payment fee estimation
  const { paymentFee, paymentFeeLoading, paymentFeeError } = useEstimatePaymentFee({
    paymentType: PaymentType.Withdrawal,
    amount: Number(amount),
  });

  // Validation
  const { errors } = useWithdrawalValidation({
    amount,
    paymentFeeError,
    paymentFee,
  });

  // Actions
  const actions = useWithdrawalActions({
    amount,
    setAmount: state.setAmount,
    setIsLoading: state.setIsLoading,
    setError: state.setError,
    setCurrentStep: state.setCurrentStep,
    setSessionData: state.setSessionData,
    setBanksList: state.setBanksList,
    setSelectedBank: state.setSelectedBank,
    setAccountNumber: state.setAccountNumber,
    sessionData,
    selectedBank,
    accountNumber,
    banksList,
    currencyId: currency.currency_id,
    currencyPrecision: currency.precision,
    mfaCode,
    isRtcEnabled,
    setIsRtcEnabled: state.setIsRtcEnabled,
  });

  // Compose withdrawal step info
  const withdrawalStep: WithdrawalStep = {
    currentStep,
    stepLoading: isLoading,
    stepError: error,
  };

  const userUseMfa = mfa_status === ToggleSwitch.On;
  const isMfaRequired = paymentsRoutes?.verification_type === VerificationType.Totp;

  return {
    // Amount
    amount,

    // Payment fee
    paymentFee,
    paymentFeeLoading,

    // Balance
    balance,

    // Validation
    errors,

    // Actions
    handleAmountChange: actions.handleAmountChange,
    handleStepChange: actions.handleStepChange,
    handleBankSelection: actions.handleBankSelection,
    handleRtcChange: actions.handleRtcChange,

    // Step state
    withdrawalStep,

    // Bank data
    banksList,
    selectedBank,
    accountNumber,

    // MFA
    mfaCode,
    setMfaCode,
    userUseMfa,
    isMfaRequired,

    // RTC
    isRtcEnabled,
  };
};
